import React, { useCallback, useEffect, useRef, useState } from "react";
import { T, useT } from "@transifex/react";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import { Button } from "storybook/components/button";
import { Loading } from "components/loading";
import { useAdminContent } from "context/admin-content";
import { useRiskReport } from "context/risk-report";
import { useRiskProfile } from "context/risk-profile";
import { usePatient } from "context/advert/use-patient";
import { RiskResultsCard } from "components/cards/RiskResultsCard";
import { RiskReportCard } from "components/cards/RiskReportCard";
import {
  RiskLevel,
  VideoData,
  ContentItem,
} from "components/cards/InterestCard";
import {
  CollapsedInterestCard,
  getColorByIndex,
} from "components/cards/CollapsedInterestCard";
import { EmailModal } from "pages/patient-results/email-modal";
import { ContentConfiguration } from "context/admin-content/types";
import {
  parseRiskReportData,
  RiskFactorItem,
} from "components/risk-summary/parse-nodes";
import toast from "storybook/components/toast";
import { SPECIALIST_HOSTNAME } from "env-vars";
import { usePatientAuth } from "context/patient-auth";
import { downloadPdf, pdfToFile } from "helpers/download-pdf";
import { SendResultsModal } from "./send-results-modal";
import {
  SimpleRequestCallModal,
  StickyRequestCallButton,
  DeclineReasonModal,
} from "components/request-call-modal";
import { useFeatureFlags } from "context/admin-content";
import { useClinicalTrialMatches } from "./use-clinical-trial-matches";
import { ClinicalTrialsList } from "./clinical-trials-list";
import { evaluateQuery } from "utils/query-evaluator";
import {
  GlobalStyle,
  PageContainer,
  ContentWrapper,
  CardsGrid,
  CardSection,
  TilesList,
  SegmentBar,
  Segment,
  ReviewedCounter,
  ReviewedEyeIcon,
  BackToTopContainer,
  HiddenPageHeading,
} from "./styles";

// Determine risk level from guidance meets_criteria value
// meets_criteria === true → "high"
// meets_criteria === false → "average"
// meets_criteria === null/undefined → "unknown"
const getRiskLevelFromGuidance = (
  guidanceKey: string,
  attributes: Record<string, any>
): RiskLevel => {
  const guidance = attributes[guidanceKey];
  if (guidance === undefined || guidance === null) {
    return "unknown";
  }
  const meetsCriteria = guidance.meets_criteria;
  if (meetsCriteria === true) {
    return "high";
  }
  if (meetsCriteria === false) {
    return "average";
  }
  return "unknown";
};

// Derive guidance key from riskhub item's interest attribute for risk level.
// Unknown or empty interest defaults to testingGuidance.
const INTEREST_TO_GUIDANCE: Record<string, string> = {
  testing: "testingGuidance",
  genetic_testing: "testingGuidance",
  mri: "mriGuidance",
  breast_mri: "mriGuidance",
  lung: "lungGuidance",
  lung_ct: "lungGuidance",
  counseling: "counselingGuidance",
  genetic_counseling: "counselingGuidance",
};

function deriveGuidanceKey(interest: string | undefined): string {
  const normalized = String(interest ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  return INTEREST_TO_GUIDANCE[normalized] ?? "testingGuidance";
}

// Filter risk factors by category and extract text
// If category is empty, show all factors
// If category is set, only show factors matching that category
const filterFactorsByCategory = (
  factors: RiskFactorItem[],
  category: string
): string[] => {
  if (!category) {
    // No category filter - return all factor texts
    return factors.map((item) => item.text);
  }
  // Filter by category and return matching factor texts
  return factors
    .filter((item) => item.category === category)
    .map((item) => item.text);
};

// Type for eligibility card data
interface EligibilityCardData {
  key: string;
  title: string;
  /** URL to an SVG icon for the interest card (riskhub items only) */
  icon?: string;
  shortText: string;
  riskLevel: RiskLevel;
  /** Raw interest attribute from the riskhub item (e.g. "testing", "mri") */
  interest: string;
  nextStepsHeader?: string;
  nextStepsHeaderContent?: string;
  bodyHtml?: string;
  showInterestCheckbox: boolean;
  interestCheckboxLabel?: string;
  videos: VideoData[];
  increasingFactors: string[];
  increasingFactorsTitle?: string;
  decreasingFactors: string[];
  decreasingFactorsTitle?: string;
  contentItems: ContentItem[];
}

export function RiskhubResults() {
  const t = useT();
  const adminData = useAdminContent();
  const featureFlags = useFeatureFlags();
  const riskProfile = useRiskProfile();
  const { patient, isLoading: patientLoading } = usePatient();
  const { logout } = usePatientAuth();
  const { reportHtml, reportPdf, isLoading: reportLoading } = useRiskReport();
  const webShareSupported = typeof navigator.share === "function";
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [interestStates, setInterestStates] = useState<Record<string, boolean>>(
    {}
  );
  const [viewedCards, setViewedCards] = useState<Set<string>>(new Set());
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [expandedCardKey, setExpandedCardKey] = useState<string | null>(null);
  const [decliningCard, setDecliningCard] = useState<{
    key: string;
    interest: string;
    title: string;
  } | null>(null);
  const [isReportCardExpanded, setIsReportCardExpanded] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const supportsWebShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";
  const pageRef = useRef<HTMLDivElement | null>(null);

  const {
    trials: clinicalTrials,
    total: clinicalTrialTotal,
    isLoading: trialsLoading,
    error: trialsError,
  } = useClinicalTrialMatches();

  const isLoading = adminData?.isLoading || reportLoading || patientLoading;

  // Call modal handlers
  const handleOpenCallModal = useCallback(() => {
    setIsCallModalOpen(true);
  }, []);

  const handleCloseCallModal = useCallback(() => {
    setIsCallModalOpen(false);
  }, []);

  // Toggle card expansion and mark as viewed
  const handleToggleCard = useCallback((key: string) => {
    setExpandedCardKey((prev) => {
      const isOpening = prev !== key;
      if (isOpening) {
        setViewedCards((v) => new Set(v).add(key));
      }
      return isOpening ? key : null;
    });
  }, []);

  // Download report handler
  const handleDownloadReport = useCallback(() => {
    downloadPdf({ reportPdf });
  }, [reportPdf]);

  // Share report handler — uses native Web Share API with PDF file when
  // supported, otherwise falls back to the SendResultsModal.
  const handleShareReport = useCallback(async () => {
    if (navigator.share) {
      try {
        const file = pdfToFile(reportPdf);
        const canShareFile = file && navigator.canShare?.({ files: [file] });

        if (canShareFile) {
          await navigator.share({
            title: t("My Risk Assessment Report"),
            files: [file],
          });
        } else {
          await navigator.share({
            title: t("My Risk Assessment Report"),
            text: t("View my personalized cancer risk assessment report."),
            url: window.location.href,
          });
        }
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          setIsSendModalOpen(true);
        }
      }
    } else {
      setIsSendModalOpen(true);
    }
  }, [reportPdf, t]);

  // Email-to-self handler — fires the existing email endpoint and toasts.
  // Only wired up when Web Share is supported (otherwise the fallback modal
  // already offers this option).
  const handleEmailToSelf = useCallback(async () => {
    try {
      const patientId = localStorage.getItem("ciq_id");
      const idToken = localStorage.getItem("id_token");
      const response = await fetch(
        `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}/risk_reports/current/email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      if (response.ok) {
        toast.success(t("Results sent to your email"));
      } else if (response.status === 401) {
        logout();
      } else {
        toast.error(t("Unable to send email. Please try again."));
      }
    } catch {
      toast.error(t("Unable to send email. Please try again."));
    }
  }, [t, logout]);

  const handleToggleReportCard = useCallback(() => {
    setIsReportCardExpanded((prev) => !prev);
  }, []);

  const handleEmailReport = useCallback(() => {
    setIsEmailModalOpen(true);
  }, []);

  const handleCloseEmailModal = useCallback(() => {
    setIsEmailModalOpen(false);
  }, []);

  // Handle scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollButton(
        window.document.body.clientHeight / 3 <= window.scrollY
      );
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleBackToTopClick = useCallback(() => {
    pageRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const handleInterestChange = useCallback(
    (key: string, interest: string, title: string) => (checked: boolean) => {
      if (checked) {
        setInterestStates((prev) => ({ ...prev, [key]: true }));
      } else {
        setDecliningCard({ key, interest, title });
      }
    },
    []
  );

  const handleDeclineCancelled = useCallback(() => {
    setDecliningCard(null);
  }, []);

  const handleDeclineConfirmed = useCallback(() => {
    if (decliningCard) {
      setInterestStates((prev) => ({ ...prev, [decliningCard.key]: false }));
    }
    setDecliningCard(null);
  }, [decliningCard]);

  if (isLoading) {
    return (
      <PageContainer>
        <Loading />
      </PageContainer>
    );
  }

  // Parse risk report data
  const { status, heading, subHeading, openingText, increasing, decreasing } =
    reportHtml
      ? parseRiskReportData(reportHtml)
      : {
          status: null,
          heading: null,
          subHeading: null,
          openingText: null,
          increasing: [],
          decreasing: [],
        };

  // Get risk profile attributes for eligibility cards
  const riskProfileAttributes = riskProfile?.riskProfile?.attributes || {};
  const riskResultKey = riskProfile?.riskResultKey;

  // Combine risk profile attributes and patient data for query evaluation
  // This allows queries to access both riskProfile.* and patient.* fields
  const queryContext = {
    // Risk profile data (guidance, risks, etc.)
    riskProfile: riskProfileAttributes,
    // Flatten risk profile attributes at root level for backward compatibility
    ...riskProfileAttributes,
    // Patient demographic and preference data (expanded with aggregate format)
    patient: patient
      ? {
          // Basic info
          firstName: patient.first_name,
          lastName: patient.last_name,
          dateOfBirth: patient.date_of_birth,
          phoneNumber: patient.phone_number,
          address1: patient.address1,
          address2: patient.address2,
          city: patient.city,
          state: patient.state,
          zipCode: patient.zip_code,
          preferredAppointmentTime: patient.preferred_appointment_time,
          screeningAppointmentsInterest:
            patient.screening_appointments_interest,
          // Expanded aggregate data
          age: patient.age,
          cancers: patient.cancers || [],
          pastGeneticTest: patient.pastGeneticTest || null,
          geneticTestResults: patient.geneticTestResults || [],
          demographic: patient.demographic || null,
          lifestyle: patient.lifestyle || null,
          tc8Scores: patient.tc8Scores || null,
        }
      : null,
  };

  // Filter content configurations based on query evaluation
  const filterContentConfigurations = (
    configs: ContentConfiguration[],
    context: Record<string, any>
  ): ContentItem[] => {
    return configs
      .filter((config) => evaluateQuery(config.query, context))
      .map((config) => ({
        content: config.content || undefined,
        videoUrl: config.videoUrl?.trim() || undefined,
        videoTitle: config.videoTitle || undefined,
        isAiPrompt: config.isAiPrompt || false,
      }));
  };

  // Build eligibility cards data
  const eligibilityCards: EligibilityCardData[] = [];

  // Check if the location setting is enabled
  // Card display is now driven by settings exclusively
  // meets_criteria only determines the risk level/appearance of the card
  const isSettingEnabled = (settings: { enabled: boolean } | undefined) => {
    return settings?.enabled === true;
  };

  (adminData?.riskhubItems ?? []).forEach((item, i) => {
    if (!isSettingEnabled(item)) return;
    // Applicable patients query drives risk level: true = high risk, false = average risk.
    // When empty, fall back to guidance-based risk level.
    const hasApplicablePatientsQuery =
      (item.applicablePatients ?? "").trim() !== "";
    const applicablePatientsMatches = evaluateQuery(
      item.applicablePatients ?? "",
      queryContext
    );
    const guidanceKey = deriveGuidanceKey(item.interest);
    const itemRiskLevel = hasApplicablePatientsQuery
      ? applicablePatientsMatches
        ? "high"
        : "average"
      : getRiskLevelFromGuidance(guidanceKey, riskProfileAttributes);
    const isItemAvgRisk = itemRiskLevel === "average";
    const riskSpecificShortText = isItemAvgRisk
      ? item.shortTextAvg
      : item.shortText;
    if (!riskSpecificShortText?.trim()) return;
    const itemShowInterest = isItemAvgRisk
      ? item.showSchedulerAvg
      : item.showScheduler;
    eligibilityCards.push({
      key: `riskhub-${i}`,
      title: item.title || t("Risk area"),
      icon: item.icon?.trim() || undefined,
      shortText: isItemAvgRisk ? item.shortTextAvg || "" : item.shortText || "",
      riskLevel: itemRiskLevel,
      interest: item.interest || "",
      nextStepsHeader: isItemAvgRisk
        ? item.nextStepsHeaderAvg || undefined
        : item.nextStepsHeader || undefined,
      nextStepsHeaderContent: isItemAvgRisk
        ? item.nextStepsHeaderContentAvg || undefined
        : item.nextStepsHeaderContent || undefined,
      bodyHtml: isItemAvgRisk
        ? item.bodyHtmlAvg || undefined
        : item.bodyHtml || undefined,
      showInterestCheckbox: itemShowInterest,
      interestCheckboxLabel: item.interestIndicatorLabel || undefined,
      videos:
        item.videoEnabled && item.videoUrl
          ? [
              {
                id: `riskhub-${i}-video`,
                title: item.videoTitle || item.title || "",
                src: item.videoUrl,
              },
            ]
          : [],
      increasingFactors: filterFactorsByCategory(increasing, item.category),
      increasingFactorsTitle: item.increasingFactorsTitle || undefined,
      decreasingFactors: filterFactorsByCategory(decreasing, item.category),
      decreasingFactorsTitle: item.decreasingFactorsTitle || undefined,
      contentItems: filterContentConfigurations(
        item.contentConfigurations,
        queryContext
      ),
    });
  });

  // Note: Average Risk card is disabled - if no category cards are enabled,
  // only the Risk Results card will be displayed

  // Sort cards by risk level: high first, then average, then unknown
  const riskLevelOrder: Record<RiskLevel, number> = {
    high: 0,
    average: 1,
    unknown: 2,
  };
  eligibilityCards.sort(
    (a, b) => riskLevelOrder[a.riskLevel] - riskLevelOrder[b.riskLevel]
  );

  // Count cards by risk level for the summary card
  const actionRequiredCount = eligibilityCards.filter(
    (card) => card.riskLevel === "high"
  ).length;
  const onTrackCount = eligibilityCards.filter(
    (card) => card.riskLevel !== "high"
  ).length;

  // Determine if the "Request a Call" button should be shown
  // It's visible if at least one displayed interest card has showInterestCheckbox enabled
  const showRequestCallButton = eligibilityCards.some(
    (card) => card.showInterestCheckbox
  );

  // Cards the user has selected for follow-up (where interest is expressed)
  const selectedForFollowUp = eligibilityCards.filter(
    (card) => card.showInterestCheckbox && (interestStates[card.key] ?? true)
  );
  const selectedForFollowUpCount = selectedForFollowUp.length;

  // Count reviewed cards
  const reviewedCount = eligibilityCards.filter((card) =>
    viewedCards.has(card.key)
  ).length;

  return (
    <>
      <GlobalStyle />
      <PageContainer ref={pageRef}>
        <HiddenPageHeading>
          <T _str="Patient Results Page" />
        </HiddenPageHeading>

        <ContentWrapper>
          <CardsGrid>
            {/* Risk Results Summary Card */}
            <CardSection>
              <RiskResultsCard
                title={heading || t("Your Personalized Risk Assessment")}
                statusDescription={openingText || subHeading || undefined}
                actionRequiredCount={actionRequiredCount}
                onTrackCount={onTrackCount}
                onDownload={handleDownloadReport}
                onEmail={webShareSupported ? handleEmailToSelf : undefined}
                onShare={handleShareReport}
                data-testid="RiskhubResults__SummaryCard"
              />
            </CardSection>

            {/* Segmented indicator bar */}
            {eligibilityCards.length > 0 && (
              <SegmentBar data-testid="RiskhubResults__SegmentBar">
                {eligibilityCards.map((card, index) => {
                  const isViewed = viewedCards.has(card.key);
                  return (
                    <Segment
                      key={card.key}
                      $color={getColorByIndex(index).border}
                      $isViewed={isViewed}
                      onClick={() => isViewed && handleToggleCard(card.key)}
                      aria-label={`${card.title}${
                        isViewed ? " - click to expand" : ""
                      }`}
                      data-testid={`RiskhubResults__Segment-${card.key}`}
                    />
                  );
                })}
              </SegmentBar>
            )}

            {/* Reviewed counter */}
            {eligibilityCards.length > 0 && (
              <ReviewedCounter data-testid="RiskhubResults__ReviewedCounter">
                <ReviewedEyeIcon />
                <T
                  _str="{reviewed}/{total} reviewed"
                  reviewed={reviewedCount}
                  total={eligibilityCards.length}
                />
              </ReviewedCounter>
            )}

            {/* Flat list of all tiles */}
            {eligibilityCards.length > 0 && (
              <TilesList data-testid="RiskhubResults__TilesList">
                {eligibilityCards.map((card, index) => (
                  <CollapsedInterestCard
                    key={card.key}
                    cardKey={card.key}
                    colorIndex={index}
                    title={card.title}
                    shortText={card.shortText}
                    riskLevel={card.riskLevel}
                    isViewed={viewedCards.has(card.key)}
                    isExpanded={expandedCardKey === card.key}
                    onToggle={() => handleToggleCard(card.key)}
                    isInterested={interestStates[card.key] ?? true}
                    showInterestToggle={card.showInterestCheckbox}
                    onInterestChange={handleInterestChange(
                      card.key,
                      card.interest,
                      card.title
                    )}
                    icon={card.icon}
                    nextStepsHeader={card.nextStepsHeader}
                    nextStepsHeaderContent={card.nextStepsHeaderContent}
                    bodyHtml={card.bodyHtml}
                    videos={card.videos}
                    contentItems={card.contentItems}
                    increasingFactors={card.increasingFactors}
                    increasingFactorsTitle={card.increasingFactorsTitle}
                    decreasingFactors={card.decreasingFactors}
                    decreasingFactorsTitle={card.decreasingFactorsTitle}
                    interestLabel={card.interestCheckboxLabel}
                    interestIcon={adminData?.riskhubContactRequestIcon}
                    data-testid={`RiskhubResults__CollapsedCard-${card.key}`}
                  />
                ))}
              </TilesList>
            )}

            {/* Clinical Trials Card */}
            {featureFlags?.enableClinicalTrialMatching && (
              <CollapsedInterestCard
                cardKey="clinical-trials"
                colorIndex={eligibilityCards.length}
                title={t("Possible clinical trials")}
                shortText={
                  trialsLoading
                    ? t("Searching for clinical trials...")
                    : trialsError
                    ? t("Unable to load clinical trial matches at this time")
                    : clinicalTrialTotal > 0
                    ? t("{count} clinical trials may be available for you", {
                        count: clinicalTrialTotal,
                      })
                    : t("No matching clinical trials found at this time")
                }
                riskLevel="average"
                showBadges={false}
                isViewed={viewedCards.has("clinical-trials")}
                isExpanded={expandedCardKey === "clinical-trials"}
                onToggle={() => handleToggleCard("clinical-trials")}
                isInterested={false}
                showInterestToggle={false}
                contentItems={[]}
                data-testid="RiskhubResults__ClinicalTrialsCard"
              >
                {trialsError ? (
                  <p style={{ color: "#b91c1c", fontSize: 14 }}>
                    {t(
                      "We were unable to load clinical trial matches. Please try again later or contact your care team for assistance."
                    )}
                  </p>
                ) : (
                  clinicalTrials.length > 0 && (
                    <ClinicalTrialsList trials={clinicalTrials} />
                  )
                )}
              </CollapsedInterestCard>
            )}

            {/* Risk Report Card - always last */}
            {reportHtml && (
              <CardSection>
                <RiskReportCard
                  isExpanded={isReportCardExpanded}
                  onToggle={handleToggleReportCard}
                  onDownload={handleDownloadReport}
                  onShare={supportsWebShare ? handleShareReport : undefined}
                  onEmail={supportsWebShare ? undefined : handleEmailReport}
                  reportHtml={reportHtml}
                  data-testid="RiskhubResults__RiskReportCard"
                />
              </CardSection>
            )}
          </CardsGrid>
        </ContentWrapper>

        <BackToTopContainer $show={showScrollButton}>
          <Button
            theme="default"
            variant="text"
            color="inherit"
            onClick={handleBackToTopClick}
            startIcon={<ArrowUpwardIcon color="primary" />}
          >
            <T _str="Back to top" />
          </Button>
        </BackToTopContainer>

        {/* Sticky Request a Call Button */}
        {showRequestCallButton && (
          <StickyRequestCallButton
            onClick={handleOpenCallModal}
            selectedCount={selectedForFollowUpCount}
            buttonText={adminData?.riskhubContactRequestPrompt}
            buttonIcon={adminData?.riskhubContactRequestIcon}
            data-testid="RiskhubResults__RequestCallButton"
          />
        )}

        {/* Send Results Modal (fallback for browsers without Web Share) */}
        <SendResultsModal
          isOpen={isSendModalOpen}
          onClose={() => setIsSendModalOpen(false)}
          reportPdf={reportPdf}
        />

        {/* Request a Call Modal */}
        <SimpleRequestCallModal
          isOpen={isCallModalOpen}
          onClose={handleCloseCallModal}
          selectedCards={selectedForFollowUp.map((card) => ({
            interest: card.interest,
          }))}
          specialistOnly={!!featureFlags.removeAccessToNavigatorApp}
          title={adminData?.riskhubContactRequestPrompt}
          showAvailability={adminData?.riskhubCollectAvailability ?? true}
        />

        {/* Decline Reason Modal — shown when patient unchecks an interest */}
        <DeclineReasonModal
          isOpen={!!decliningCard}
          onClose={handleDeclineCancelled}
          onDeclined={handleDeclineConfirmed}
          interest={decliningCard?.interest ?? ""}
          cardTitle={decliningCard?.title ?? ""}
        />

        {/* Email Report Modal */}
        {isEmailModalOpen && <EmailModal onClose={handleCloseEmailModal} />}
      </PageContainer>
    </>
  );
}
